import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import {
  ArrowRight,
  Baby,
  Boxes,
  Check,
  ChevronDown,
  CircleUserRound,
  Clock3,
  Download,
  Droplets,
  ExternalLink,
  Gift,
  Grid2X2,
  HeartHandshake,
  Home,
  Instagram,
  Leaf,
  MapPin,
  Menu,
  MessageCircle,
  Moon,
  PackageCheck,
  Pencil,
  Phone,
  Plus,
  Search,
  Settings,
  ShieldCheck,
  Shirt,
  ShoppingBag,
  Store,
  Sun,
  Trash2,
  X
} from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { Link, NavLink, Route, Routes, useLocation, useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import { format } from "date-fns";
import { categories, money, productMessage, products as fallbackProducts, testimonials, whatsapp, type Product } from "./data";
import { enquirySchema, type Enquiry } from "./schema";

const PHONE = "+234 706 202 5462";

function usePageTitle(title: string) {
  useEffect(() => {
    document.title = `${title} | Pumbaalicious Store`;
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [title]);
}

function TuskArc({ light = false }: { light?: boolean }) {
  return (
    <svg className={`tusk-arc ${light ? "light" : ""}`} viewBox="0 0 132 24" aria-hidden="true">
      <path d="M4 5c22 2 38 8 62 17C90 13 106 7 128 5" />
      <circle cx="66" cy="22" r="1.8" />
    </svg>
  );
}

function Logo() {
  return (
    <Link to="/" className="logo" aria-label="Pumbaalicious Store home">
      <span className="logo-mark"><span>P</span></span>
      <span>
        <strong>Pumbaalicious</strong>
        <small>Family Store · Egbeda</small>
      </span>
    </Link>
  );
}

function ThemeToggle({ compact = false }: { compact?: boolean }) {
  const [theme, setTheme] = useState<"light" | "dark">(
    () => document.documentElement.dataset.theme === "dark" ? "dark" : "light"
  );

  const toggle = () => {
    const next = theme === "dark" ? "light" : "dark";
    document.documentElement.dataset.theme = next;
    localStorage.setItem("pumba-theme", next);
    setTheme(next);
  };

  return (
    <button className={`theme-toggle ${compact ? "compact" : ""}`} onClick={toggle} aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`} title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}>
      {theme === "dark" ? <Sun /> : <Moon />}
      {!compact && <span>{theme === "dark" ? "Light" : "Dark"}</span>}
    </button>
  );
}

const nav = [
  ["Shop", "/shop"],
  ["Our story", "/about"],
  ["Delivery", "/delivery"],
  ["Reviews", "/reviews"],
  ["Contact", "/contact"]
];

function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <div className="announcement">
        <span><Clock3 size={15} /> 10+ years serving Egbeda families</span>
        <span className="announcement-address"><MapPin size={15} /> House 5, Gowon Estate</span>
      </div>
      <header className="site-header">
        <Logo />
        <nav className={open ? "nav-open" : ""} aria-label="Main navigation">
          {nav.map(([label, to]) => (
            <NavLink key={to} to={to} className={({ isActive }) => isActive ? "active" : ""}>{label}</NavLink>
          ))}
          <a className="button button-small whatsapp-nav" href={whatsapp("Hi Kemi, I'd like to ask about what you have in store.")} target="_blank" rel="noreferrer">
            <MessageCircle size={17} /> WhatsApp us
          </a>
        </nav>
        <ThemeToggle compact />
        <button className="menu-button" onClick={() => setOpen(!open)} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}>
          {open ? <X /> : <Menu />}
        </button>
      </header>
    </>
  );
}

function Footer() {
  return (
    <footer>
      <div className="footer-inner">
        <div className="footer-brand">
          <Logo />
          <p>One warm, trusted shop for the whole family. No wahala, we get am.</p>
          <TuskArc light />
        </div>
        <div>
          <h3>Come by</h3>
          <p>House 5, Gowon Estate<br />Egbeda, Alimosho, Lagos</p>
          <p>Mon–Fri: 10am–5pm<br />Saturday: 11am–4pm</p>
        </div>
        <div>
          <h3>Quick links</h3>
          <Link to="/shop">Browse the shop</Link>
          <Link to="/faq">Frequently asked</Link>
          <Link to="/privacy">Privacy & terms</Link>
        </div>
        <div>
          <h3>Let’s talk</h3>
          <a href="tel:+2347062025462"><Phone size={16} /> {PHONE}</a>
          <a href={whatsapp("Hi Kemi, I found Pumbaalicious online and would like to make an enquiry.")} target="_blank" rel="noreferrer"><MessageCircle size={16} /> Chat on WhatsApp</a>
          <a href="#" aria-label="Instagram"><Instagram size={16} /> Instagram</a>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© {new Date().getFullYear()} Pumbaalicious Store</span>
        <a href="https://instagram.com/buildwithduke" target="_blank" rel="noreferrer">Website design by buildwithduke <ExternalLink size={12} /></a>
      </div>
    </footer>
  );
}

function Layout({ children }: { children: ReactNode }) {
  const location = useLocation();
  return (
    <>
      <Header />
      <main id="main">{children}</main>
      <Footer />
      <a className="floating-whatsapp" href={whatsapp("Hi Kemi, I found Pumbaalicious online and would like to make an enquiry.")} target="_blank" rel="noreferrer" aria-label="Chat with Kemi on WhatsApp">
        <MessageCircle /><span>Chat with Kemi</span>
      </a>
      {location.pathname === "/" && <CookieNote />}
    </>
  );
}

function CookieNote() {
  const [visible, setVisible] = useState(() => localStorage.getItem("pumba-consent") !== "accepted");
  if (!visible) return null;
  return (
    <div className="cookie-note" role="dialog" aria-label="Privacy preference">
      <div><ShieldCheck /><p><strong>Your privacy matters.</strong><br />We only use essential site storage and anonymous performance data.</p></div>
      <button onClick={() => { localStorage.setItem("pumba-consent", "accepted"); setVisible(false); }}>Got it</button>
    </div>
  );
}

const categoryIcons = [Shirt, Home, Droplets, Baby, HeartHandshake, Leaf, Gift];

function Reveal({ children, delay = 0, className = "" }: { children: ReactNode; delay?: number; className?: string }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduced ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: reduced ? 0 : 0.55, delay }}
    >
      {children}
    </motion.div>
  );
}

function Hero() {
  const reduced = useReducedMotion();
  const featured = categories.slice(0, 5);
  return (
    <section className="hero section-pad">
      <motion.div className="hero-stage" initial={reduced ? false : { opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .6 }}>
        <div className="hero-copy">
          <div className="eyebrow"><span /> Your family shop in Egbeda</div>
          <h1>Everything your family needs, <em>one WhatsApp away.</em></h1>
          <TuskArc />
          <p>Genuine everyday finds, thoughtful service and the kind of honesty that keeps neighbours coming back.</p>
          <div className="button-row hero-actions">
            <a className="button" href={whatsapp("Hi Kemi, I'd like to shop with Pumbaalicious. What do you have available?")} target="_blank" rel="noreferrer">
              <MessageCircle size={19} /> Ask Kemi what’s in store
            </a>
            <Link className="text-link" to="/shop">Browse the shop <ArrowRight size={17} /></Link>
          </div>
          <div className="hero-proof">
            <div className="avatar-stack" aria-hidden="true"><span>D</span><span>F</span><span>+8</span></div>
            <div><span className="stars" aria-label="5 out of 5 stars">★★★★★</span><small>Loved by local families for 10+ years</small></div>
          </div>
        </div>
        <div className="hero-visual" aria-label="Meet Kemi, the owner of Pumbaalicious">
          <span className="hero-sun" aria-hidden="true" />
          <div className="hero-chat-card">
            <MessageCircle aria-hidden="true" />
            <span><small>Shopping made simple</small><strong>Tell me what you need.</strong></span>
          </div>
          <motion.figure className="hero-photo" initial={reduced ? false : { opacity: 0, scale: .94, rotate: -2 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ delay: .18, duration: .55 }}>
            <img src="/kemi-esan.jpg" alt="Kemi Esan, owner of Pumbaalicious Store" />
            <figcaption className="photo-note"><strong>Hi, I’m Kemi!</strong><span>Your neighbour with the good stuff.</span></figcaption>
          </motion.figure>
          <span className="years-badge"><b>10+</b> years<br />of trust</span>
          <div className="hero-location"><MapPin aria-hidden="true" /><span><small>Come by the shop</small><strong>Gowon Estate, Egbeda</strong></span></div>
        </div>
      </motion.div>
      <div className="hero-category-rail" aria-label="Popular shop categories">
        {featured.map((category, i) => {
          const Icon = categoryIcons[i];
          return (
            <motion.div className="hero-category-item" key={category.slug} initial={reduced ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .28 + i * .07 }}>
              <Link className={`hero-category ${category.tone}`} to={`/shop/${category.slug}`} aria-label={`Shop ${category.name}`}>
                <span className="hero-category-icon"><Icon aria-hidden="true" /></span>
                <span><small>{category.short}</small><strong>{category.name}</strong></span>
                <ArrowRight aria-hidden="true" />
              </Link>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function ValueStrip() {
  return (
    <section className="value-strip" aria-label="Why customers choose us">
      <div><ShieldCheck /><span><strong>Genuine products</strong>What you see is what you get</span></div>
      <div><Clock3 /><span><strong>10+ years trusted</strong>Serving Egbeda since 2015</span></div>
      <div><MessageCircle /><span><strong>Easy WhatsApp orders</strong>Ask, confirm, done — no wahala</span></div>
    </section>
  );
}

function SectionHeading({ kicker, title, copy, action }: { kicker: string; title: string; copy?: string; action?: ReactNode }) {
  return (
    <div className="section-heading">
      <div>
        <span className="eyebrow"><span /> {kicker}</span>
        <h2>{title}</h2>
        <TuskArc />
        {copy && <p>{copy}</p>}
      </div>
      {action}
    </div>
  );
}

function CategoryGrid() {
  return (
    <section className="section-pad categories-section">
      <SectionHeading kicker="A little bit of everything" title="Shop by category" copy="From the everyday essentials to a lovely little treat, find it without scrolling through endless status updates." action={<Link className="text-link" to="/shop">View all products <ArrowRight size={17} /></Link>} />
      <div className="category-grid">
        {categories.map((cat, i) => {
          const Icon = categoryIcons[i];
          return (
            <Reveal key={cat.slug} delay={i * .05}>
              <Link className={`category-card rotated ${cat.tone}`} to={`/shop/${cat.slug}`}>
                <span className="icon-orbit"><Icon /></span>
                <span className="card-index">0{i + 1}</span>
                <h3>{cat.name}</h3>
                <p>{cat.description}</p>
                <span className="round-arrow"><ArrowRight /></span>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function FounderStory() {
  return (
    <section className="story-section section-pad">
      <Reveal className="story-photo-wrap">
        <div className="story-photo"><img src="/kemi-esan.jpg" alt="Kemi Esan smiling" /></div>
        <div className="scribble-note">Family first,<br />always.</div>
      </Reveal>
      <Reveal className="story-copy" delay={.1}>
        <span className="eyebrow"><span /> Your trusted neighbour</span>
        <blockquote>“I’ve always believed that business should be simple: be honest, be helpful, and give people exactly what they asked for.”</blockquote>
        <TuskArc />
        <p>I started Pumbaalicious in 2015 to make shopping for the whole family a little easier. Today, I’m still here in Egbeda — helping customers find what they need and going the extra mile when I can.</p>
        <Link className="button button-outline" to="/about">Meet Kemi <ArrowRight size={17} /></Link>
      </Reveal>
    </section>
  );
}

function ProductArt({ product }: { product: Product }) {
  return (
    <div className="product-art">
      <img src={product.image} alt={product.name} loading="lazy" width="1200" height="900" />
    </div>
  );
}

async function loadProducts(): Promise<Product[]> {
  const saved = localStorage.getItem("pumba-admin-products");
  const localProducts = saved ? JSON.parse(saved) as Product[] : null;
  if (localProducts?.length) return localProducts;
  const response = await fetch("/api/products");
  if (!response.ok) throw new Error("Catalogue unavailable");
  const data = await response.json() as { products?: Product[] };
  return data.products?.length
    ? data.products.map(product => {
      const fallback = fallbackProducts.find(item => item.id === product.id);
      return { ...fallback, ...product, image: product.image || fallback?.image || "/products/golden-gift-pair.webp" } as Product;
    })
    : fallbackProducts;
}

function useProducts() {
  return useQuery({ queryKey: ["products"], queryFn: loadProducts, placeholderData: fallbackProducts });
}

function ProductCard({ product }: { product: Product }) {
  return (
    <article className="product-card rotated">
      <Link to={`/product/${product.slug}`} className="product-image-link">
        <ProductArt product={product} />
        {product.handmade && <span className="product-tag">Handmade</span>}
      </Link>
      <div className="product-card-body">
        <span className="stock"><i /> {product.stock}</span>
        <h3><Link to={`/product/${product.slug}`}>{product.name}</Link></h3>
        <p>{product.description}</p>
        <div className="product-price-row"><strong>{money(product.price)}</strong><small>Availability confirmed on chat</small></div>
        <a className="button product-button" href={productMessage(product)} target="_blank" rel="noreferrer"><MessageCircle size={17} /> Order on WhatsApp</a>
      </div>
    </article>
  );
}

function FeaturedProducts() {
  const { data = fallbackProducts } = useProducts();
  const featured = data.filter(product => product.featured).slice(0, 4);
  return (
    <section className="featured section-pad">
      <SectionHeading kicker="Lovely finds, ready to go" title="Customer favourites" copy="A few things people are asking for right now. Message Kemi to confirm colours, sizes and what’s in stock." action={<Link className="text-link" to="/shop">Browse the full shop <ArrowRight size={17} /></Link>} />
      <div className="product-grid">
        {featured.map((product, i) => <Reveal key={product.id} delay={i * .06}><ProductCard product={product} /></Reveal>)}
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="testimonials section-pad">
      <SectionHeading kicker="Straight from the neighbourhood" title="Kind words, honestly earned" />
      <div className="testimonial-grid">
        {testimonials.map((item, i) => (
          <Reveal key={item.id} delay={i * .08}>
            <figure className="testimonial-card rotated">
              <div className="stars" aria-label="5 out of 5 stars">★★★★★</div>
              <blockquote>“{item.quote}”</blockquote>
              <figcaption><span>{item.name.slice(0, 1)}</span><div><strong>{item.name}</strong><small>{item.source}</small></div></figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function DeliveryPreview() {
  const items = [
    { icon: MapPin, title: "Egbeda & Alimosho", text: "Fast local delivery. Fee confirmed before you pay." },
    { icon: PackageCheck, title: "Lagos-wide", text: "We can arrange delivery across Lagos for suitable orders." },
    { icon: ShoppingBag, title: "Pick up in store", text: "Collect at House 5, Gowon Estate when it suits you." }
  ];
  return (
    <section className="delivery-preview section-pad">
      <div className="delivery-copy">
        <span className="eyebrow"><span /> From our door to yours</span>
        <h2>However you need it, we’ll help it get there.</h2>
        <TuskArc />
        <p>Chat with Kemi first. We’ll confirm availability, your delivery fee and the easiest way to pay before anything moves.</p>
        <div className="payment-pills"><span>Bank transfer</span><span>Cash on collection</span></div>
        <Link to="/delivery" className="text-link">See delivery details <ArrowRight size={17} /></Link>
      </div>
      <div className="delivery-list">
        {items.map(({ icon: Icon, title, text }, i) => <Reveal key={title} delay={i * .08}><div className="delivery-item"><span><Icon /></span><div><strong>{title}</strong><p>{text}</p></div><Check /></div></Reveal>)}
      </div>
    </section>
  );
}

const faqs = [
  ["Does Pumbaalicious deliver in Lagos?", "Yes. We arrange local delivery around Egbeda and Alimosho, and can deliver suitable orders across Lagos. Send your location on WhatsApp for a clear fee and timing."],
  ["How do I pay for my order?", "Once we confirm that your items are available, you can pay by bank transfer or cash when collecting. We never ask you to pay before stock and delivery details are clear."],
  ["Do you sell adult diapers and elderly-care items?", "Yes. We stock respectful, practical personal-care products for older family members. Ask Kemi about current brands, sizes and pack options."]
];

function FAQList({ limit }: { limit?: number }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="faq-list">
      {faqs.slice(0, limit).map(([question, answer], i) => (
        <div className={`faq-item ${open === i ? "open" : ""}`} key={question}>
          <button onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i}><span>{question}</span><ChevronDown /></button>
          <div><p>{answer}</p></div>
        </div>
      ))}
    </div>
  );
}

function FAQPreview() {
  return (
    <section className="faq-preview section-pad">
      <div><SectionHeading kicker="Good to know" title="Questions? We’ve got you." copy="Clear answers before you order — and if yours isn’t here, Kemi is only a message away." /><a className="button button-outline" href={whatsapp("Hi Kemi, I have a question about shopping with Pumbaalicious.")} target="_blank" rel="noreferrer">Ask Kemi a question</a></div>
      <FAQList limit={3} />
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="final-cta section-pad">
      <Store className="cta-store-mark one" aria-hidden="true" /><MessageCircle className="cta-store-mark two" aria-hidden="true" />
      <span className="eyebrow light"><span /> Let’s sort your list</span>
      <h2>Need something today?<br /><em>Ask Kemi.</em></h2>
      <p>Tell us what you’re looking for. We’ll confirm what’s in stock, the price and delivery — all on WhatsApp.</p>
      <div className="button-row centered">
        <a className="button button-cream" href={whatsapp("Hi Kemi, I need help finding something for my family.")} target="_blank" rel="noreferrer"><MessageCircle /> Start a WhatsApp order</a>
        <a className="button button-ghost" href="tel:+2347062025462"><Phone /> Call {PHONE}</a>
      </div>
    </section>
  );
}

function HomePage() {
  usePageTitle("Everything your family needs");
  return (
    <>
      <Hero />
      <ValueStrip />
      <CategoryGrid />
      <FounderStory />
      <FeaturedProducts />
      <Testimonials />
      <DeliveryPreview />
      <FAQPreview />
      <FinalCTA />
    </>
  );
}

function PageHero({ kicker, title, copy }: { kicker: string; title: string; copy: string }) {
  return (
    <section className="page-hero section-pad">
      <span className="eyebrow"><span /> {kicker}</span>
      <h1>{title}</h1>
      <TuskArc />
      <p>{copy}</p>
    </section>
  );
}

function ShopPage() {
  usePageTitle("Shop the catalogue");
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data = fallbackProducts, isError } = useProducts();
  const [search, setSearch] = useState("");
  const active = categories.find(category => category.slug === slug);
  const shown = data.filter(product => (!active || product.categoryId === active.id) && product.name.toLowerCase().includes(search.toLowerCase()));
  return (
    <>
      <PageHero kicker="The whole shop, in one place" title={active ? active.name : "Find your next lovely thing"} copy={active?.description || "Browse everyday essentials and special finds, then message Kemi to confirm what’s available."} />
      <section className="shop-layout section-pad">
        <aside className="shop-filters">
          <h2>Categories</h2>
          <button className={!active ? "selected" : ""} onClick={() => navigate("/shop")}>Everything <span>{data.length}</span></button>
          {categories.map(cat => <button key={cat.slug} className={active?.slug === cat.slug ? "selected" : ""} onClick={() => navigate(`/shop/${cat.slug}`)}>{cat.name}<span>{data.filter(p => p.categoryId === cat.id).length}</span></button>)}
        </aside>
        <div className="shop-results">
          <div className="shop-tools"><div className="search-box"><Search /><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search the shop" aria-label="Search products" /></div><span>{shown.length} lovely {shown.length === 1 ? "find" : "finds"}</span></div>
          {isError && <div className="notice">You’re viewing our showroom selection while the live catalogue reconnects.</div>}
          <div className="product-grid">{shown.map(product => <ProductCard key={product.id} product={product} />)}</div>
          {!shown.length && <div className="empty-state"><ShoppingBag /><h2>Nothing matched that search</h2><p>Try another phrase or ask Kemi — it may simply not be online yet.</p></div>}
        </div>
      </section>
    </>
  );
}

function ProductPage() {
  const { slug } = useParams();
  const { data = fallbackProducts } = useProducts();
  const product = data.find(item => item.slug === slug);
  usePageTitle(product?.name || "Product");
  if (!product) return <NotFound />;
  const category = categories.find(item => item.id === product.categoryId);
  const related = data.filter(item => item.categoryId === product.categoryId && item.id !== product.id).slice(0, 3);
  return (
    <>
      <section className="product-detail section-pad">
        <div className="breadcrumbs"><Link to="/">Home</Link><span>/</span><Link to={`/shop/${category?.slug}`}>{category?.name}</Link><span>/</span><span>{product.name}</span></div>
        <div className="product-detail-grid">
          <ProductArt product={product} />
          <div className="product-info">
            <span className="stock"><i /> {product.stock}</span>
            <h1>{product.name}</h1>
            <TuskArc />
            <strong className="detail-price">{money(product.price)}</strong>
            <p>{product.description}</p>
            <ul><li><Check /> Availability confirmed before payment</li><li><Check /> Local Egbeda delivery or collection</li><li><Check /> Friendly help choosing the right option</li></ul>
            <a className="button button-wide" href={productMessage(product)} target="_blank" rel="noreferrer"><MessageCircle /> Ask Kemi to reserve this</a>
            <small className="payment-note"><ShieldCheck /> No online payment is taken. Kemi confirms your order first.</small>
          </div>
        </div>
      </section>
      {related.length > 0 && <section className="section-pad related"><SectionHeading kicker="You may also like" title="More from this shelf" /><div className="product-grid">{related.map(item => <ProductCard key={item.id} product={item} />)}</div></section>}
    </>
  );
}

function AboutPage() {
  usePageTitle("Meet Kemi");
  return (
    <>
      <PageHero kicker="Our story" title="The good shop around the corner." copy="Built on honesty, helpfulness and more than ten years of showing up for local families." />
      <section className="about-grid section-pad">
        <div className="about-image"><img src="/kemi-esan.jpg" alt="Kemi Esan, founder of Pumbaalicious Store" /><span><b>Since</b> 2015</span></div>
        <div className="prose"><h2>Hi, I’m Kemi.</h2><p>I started Pumbaalicious right here in Egbeda with a simple idea: families should have one dependable person to call when they need something.</p><p>What began small has grown through recommendations, repeat customers and the trust people place in me. That means a lot. I’m careful about what I sell, I’m honest about what’s available, and if I can help make your day easier, I will.</p><blockquote>“No long story. Just genuine products, fair service and exactly what you ordered.”</blockquote><p>Whether you’re shopping for the kids, keeping the house stocked, caring for an older parent or looking for a special handmade gift, you’re welcome here.</p><a className="button" href={whatsapp("Hi Kemi, I just read your story and would like to shop with you.")} target="_blank" rel="noreferrer">Say hello on WhatsApp</a></div>
      </section>
      <section className="values section-pad"><div><ShieldCheck /><h3>Honest, always</h3><p>Clear prices and straight answers, with no pressure.</p></div><div><HeartHandshake /><h3>Family first</h3><p>Helpful service for every generation of your family.</p></div><div><Leaf /><h3>Made personal</h3><p>Real care, including beautiful things finished by hand.</p></div></section>
    </>
  );
}

function DeliveryPage() {
  usePageTitle("Delivery & collection");
  return (
    <>
      <PageHero kicker="From our shop to your door" title="Delivery without the guesswork." copy="We confirm your items, fee and timing with you before you pay. Simple." />
      <section className="info-cards section-pad">
        <article><MapPin /><h2>Egbeda & Alimosho</h2><p>Our closest delivery zone. Send your landmark and preferred time on WhatsApp for a quick quote.</p><span>Usually same or next day</span></article>
        <article><PackageCheck /><h2>Across Lagos</h2><p>We arrange wider Lagos delivery for suitable orders. Cost depends on parcel size and your location.</p><span>Timing confirmed on chat</span></article>
        <article><ShoppingBag /><h2>Store collection</h2><p>Pick up from House 5, Gowon Estate, Egbeda after Kemi confirms that your order is ready.</p><span>No collection fee</span></article>
      </section>
      <section className="steps section-pad"><SectionHeading kicker="Three easy steps" title="How an order works" /><ol><li><b>1</b><div><strong>Send your list</strong><p>Choose an item or tell Kemi what you’re trying to find.</p></div></li><li><b>2</b><div><strong>We confirm everything</strong><p>Get availability, total price, delivery fee and timing in writing.</p></div></li><li><b>3</b><div><strong>Pay and receive</strong><p>Pay by bank transfer or cash on collection, then we get it to you.</p></div></li></ol></section>
      <FinalCTA />
    </>
  );
}

function ReviewsPage() {
  usePageTitle("Customer reviews");
  return (
    <>
      <PageHero kicker="Trusted around Egbeda" title="Real words from real customers." copy="Pumbaalicious has grown through recommendations. Here are a few reasons why." />
      <section className="review-page section-pad">
        <div className="rating-summary"><strong>5.0</strong><span className="stars">★★★★★</span><p>Verified customer rating</p></div>
        <div className="testimonial-grid">{testimonials.map(item => <figure className="testimonial-card rotated" key={item.id}><div className="stars">★★★★★</div><blockquote>“{item.quote}”</blockquote><figcaption><span>{item.name[0]}</span><div><strong>{item.name}</strong><small>{item.source}</small></div></figcaption></figure>)}</div>
      </section>
      <FinalCTA />
    </>
  );
}

function EnquiryForm() {
  const [sent, setSent] = useState(false);
  const [serverError, setServerError] = useState("");
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm<Enquiry>({ resolver: zodResolver(enquirySchema), defaultValues: { consent: false as true } });
  const submit = async (data: Enquiry) => {
    setServerError("");
    try {
      const response = await fetch("/api/leads", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(data) });
      if (!response.ok) throw new Error("Unable to send");
      setSent(true); reset();
    } catch {
      window.open(whatsapp(`Hi Kemi, I'm ${data.name}. ${data.message} My number is ${data.phone}.`), "_blank", "noopener,noreferrer");
      setSent(true);
      setServerError("The form moved your message to WhatsApp so it still reaches Kemi.");
    }
  };
  if (sent) return <div className="form-success"><span><Check /></span><h2>Message ready!</h2><p>{serverError || "Thanks — Kemi will get back to you as soon as she can."}</p><button onClick={() => setSent(false)}>Send another</button></div>;
  return (
    <form onSubmit={handleSubmit(submit)} className="contact-form">
      <label>Your name<input {...register("name")} autoComplete="name" placeholder="e.g. Ada" />{errors.name && <small>{errors.name.message}</small>}</label>
      <label>Phone number<input {...register("phone")} type="tel" autoComplete="tel" placeholder="0706 202 5462" />{errors.phone && <small>{errors.phone.message}</small>}</label>
      <label>What can we help with?<textarea {...register("message")} rows={5} placeholder="Tell Kemi what you’re looking for…" />{errors.message && <small>{errors.message.message}</small>}</label>
      <label className="checkbox"><input {...register("consent")} type="checkbox" /><span>I’m happy for Pumbaalicious to use my details only to reply to this enquiry.</span></label>
      {errors.consent && <small className="field-error">{errors.consent.message}</small>}
      <button className="button button-wide" disabled={isSubmitting}>{isSubmitting ? "Sending…" : "Send my enquiry"} <ArrowRight /></button>
    </form>
  );
}

function ContactPage() {
  usePageTitle("Contact Kemi");
  return (
    <>
      <PageHero kicker="We’re easy to reach" title="What can we help you find?" copy="Message, call or come by. For the quickest answer about stock, WhatsApp is best." />
      <section className="contact-layout section-pad">
        <div className="contact-details">
          <div><MessageCircle /><span><small>WhatsApp</small><a href={whatsapp("Hi Kemi, I’d like to make an enquiry.")} target="_blank" rel="noreferrer">Chat with Kemi</a></span></div>
          <div><Phone /><span><small>Call</small><a href="tel:+2347062025462">{PHONE}</a></span></div>
          <div><MapPin /><span><small>Visit</small><strong>House 5, Gowon Estate, Egbeda</strong></span></div>
          <div><Clock3 /><span><small>Opening hours</small><strong>Mon–Fri 10am–5pm · Sat 11am–4pm</strong></span></div>
          <div className="map-card">
            <iframe
              title="Google Maps location for Pumbaalicious Store in Gowon Estate, Egbeda"
              src="https://www.google.com/maps?q=Gowon%20Estate%2C%20Egbeda%2C%20Lagos&output=embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <a className="map-link" href="https://www.google.com/maps?q=Gowon%20Estate%2C%20Egbeda%2C%20Lagos" target="_blank" rel="noreferrer">
              Open in Google Maps <ExternalLink />
            </a>
          </div>
        </div>
        <div><h2>Send a quick enquiry</h2><p className="form-intro">Prefer a form? Leave your details and Kemi will reply using the number you provide.</p><EnquiryForm /></div>
      </section>
    </>
  );
}

function FAQPage() {
  usePageTitle("Frequently asked questions");
  return <><PageHero kicker="Clear answers, no long story" title="Frequently asked questions." copy="Everything you need to know before placing an order." /><section className="standalone-faq section-pad"><FAQList /><div className="help-card"><MessageCircle /><h2>Still wondering?</h2><p>Ask Kemi directly and get a straight answer.</p><a className="button" href={whatsapp("Hi Kemi, I have a question about Pumbaalicious.")} target="_blank" rel="noreferrer">Chat on WhatsApp</a></div></section></>;
}

function PrivacyPage() {
  usePageTitle("Privacy & terms");
  return (
    <><PageHero kicker="Plain-language policy" title="Your details stay yours." copy="How Pumbaalicious handles personal information under Nigeria’s data-protection rules." /><article className="legal prose section-pad">
      <h2>Privacy notice</h2><p>When you send an enquiry, we collect only your name, phone number and message so we can respond, confirm an order or arrange delivery. We do not sell your information or use it for unrelated marketing without asking you.</p>
      <h2>Our reason for using it</h2><p>We use your information with your consent and where necessary to provide the service you requested. Enquiry records are retained only as long as they are useful for customer service, legal or bookkeeping purposes.</p>
      <h2>Your choices</h2><p>You can ask to see, correct or delete the personal information we hold about you. Contact Kemi using the phone number on this site. We will verify that the request is yours before acting.</p>
      <h2>Payments and third parties</h2><p>We currently confirm payments outside this website. Delivery providers receive only the details necessary to complete delivery. We do not claim CAC or NAFDAC registration until confirmed business details are supplied.</p>
      <h2>Cookies and storage</h2><p>This site uses essential local storage to remember privacy preferences. Anonymous performance monitoring may be used to maintain reliability; it is not used to build advertising profiles.</p>
      <p><small>Last updated {format(new Date(), "d MMMM yyyy")}. This clear notice is designed around the Nigeria Data Protection Act 2023 and NDPR principles.</small></p>
    </article></>
  );
}

type AdminRow = Product & { updated: string };
const column = createColumnHelper<AdminRow>();

function AdminPage() {
  usePageTitle("Store admin");
  const [section, setSection] = useState<"products" | "categories" | "reviews" | "enquiries" | "settings">("products");
  const [productRows, setProductRows] = useState<Product[]>(() => {
    const saved = localStorage.getItem("pumba-admin-products");
    return saved ? JSON.parse(saved) as Product[] : fallbackProducts;
  });
  const [editing, setEditing] = useState<Product | null>(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [categoryState, setCategoryState] = useState(() => categories.map(category => ({ ...category, visible: true })));
  const [reviewState, setReviewState] = useState(() => testimonials.map(item => ({ ...item, published: true })));
  const [leadState, setLeadState] = useState([
    { id: 1, name: "Ada N.", phone: "0803 555 0142", message: "Do you have the palm slippers in size 40?", status: "New", date: "Today, 10:42" },
    { id: 2, name: "Mrs Alade", phone: "0704 220 8891", message: "Please send adult care pants options and pack sizes.", status: "New", date: "Yesterday, 16:08" },
    { id: 3, name: "Tobi", phone: "0812 400 3190", message: "Can the cleaning bundle come to Akowonjo?", status: "Replied", date: "25 Jul, 09:15" }
  ]);
  const [settingsState, setSettingsState] = useState(() => {
    const saved = localStorage.getItem("pumba-admin-settings");
    return saved ? JSON.parse(saved) as Record<string, string> : {
      phone: PHONE,
      whatsapp: "2347062025462",
      hours: "Mon–Fri 10am–5pm · Sat 11am–4pm",
      address: "House 5, Gowon Estate, Egbeda, Lagos",
      delivery: "Egbeda & Alimosho · Lagos-wide · Store collection",
      payment: "Bank transfer · Cash on collection"
    };
  });
  const [savedMessage, setSavedMessage] = useState("");

  useEffect(() => {
    localStorage.setItem("pumba-admin-products", JSON.stringify(productRows));
  }, [productRows]);

  const rows = useMemo<AdminRow[]>(() => productRows.map((product, i) => ({ ...product, updated: format(new Date(2026, 6, 27 - Math.min(i, 20)), "d MMM yyyy") })), [productRows]);
  const columns = useMemo(() => [
    column.accessor("name", { header: "Product", cell: info => <span className="admin-product"><img className="mini-art" src={info.row.original.image} alt="" />{info.getValue()}</span> }),
    column.accessor("stock", { header: "Status", cell: info => <span className="status-pill">{info.getValue()}</span> }),
    column.accessor("price", { header: "Price", cell: info => money(info.getValue()) }),
    column.accessor("updated", { header: "Updated" }),
    column.display({ id: "actions", header: "Actions", cell: info => <span className="admin-actions"><button aria-label={`Edit ${info.row.original.name}`} onClick={() => { setEditing(info.row.original); setShowProductForm(true); }}><Pencil /></button><button aria-label={`Delete ${info.row.original.name}`} onClick={() => { if (window.confirm(`Remove ${info.row.original.name} from the catalogue?`)) setProductRows(items => items.filter(item => item.id !== info.row.original.id)); }}><Trash2 /></button></span> })
  ], []);
  const table = useReactTable({ data: rows, columns, getCoreRowModel: getCoreRowModel() });

  const beginAdd = () => {
    setEditing({
      id: Math.max(0, ...productRows.map(product => product.id)) + 1,
      categoryId: 1,
      name: "",
      slug: "",
      description: "",
      price: 0,
      stock: "In stock",
      art: "gift",
      image: "/products/golden-gift-pair.webp"
    });
    setShowProductForm(true);
  };

  const saveProduct = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editing?.name.trim() || editing.price <= 0) return;
    const product = { ...editing, slug: editing.slug || editing.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") };
    setProductRows(items => items.some(item => item.id === product.id) ? items.map(item => item.id === product.id ? product : item) : [product, ...items]);
    setShowProductForm(false);
    setEditing(null);
    setSavedMessage("Catalogue saved on this device.");
    window.setTimeout(() => setSavedMessage(""), 2400);
  };

  const exportLeads = () => {
    const csv = ["Name,Phone,Message,Status,Date", ...leadState.map(lead => [lead.name, lead.phone, lead.message, lead.status, lead.date].map(value => `"${value.replaceAll("\"", "\"\"")}"`).join(","))].join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    link.download = "pumbaalicious-enquiries.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const sections = [
    { id: "products" as const, label: "Products", icon: ShoppingBag },
    { id: "categories" as const, label: "Categories", icon: Grid2X2 },
    { id: "reviews" as const, label: "Reviews", icon: HeartHandshake },
    { id: "enquiries" as const, label: "Enquiries", icon: MessageCircle, count: leadState.filter(item => item.status === "New").length },
    { id: "settings" as const, label: "Settings", icon: Settings }
  ];

  return (
    <section className="admin-shell">
      <aside className="admin-sidebar"><Logo /><nav>{sections.map(({ id, label, icon: Icon, count }) => <button key={id} className={section === id ? "active" : ""} onClick={() => setSection(id)}><Icon /> {label}{count ? <b>{count}</b> : null}</button>)}</nav><Link to="/"><ArrowRight /> Back to store</Link></aside>
      <div className="admin-main">
        <header><div><span>Store dashboard / {section}</span><h1>{section === "products" ? "Good afternoon, Kemi." : sections.find(item => item.id === section)?.label}</h1></div><div className="admin-header-actions"><ThemeToggle compact /><button className="admin-user"><CircleUserRound /><span>KE</span></button></div></header>
        {savedMessage && <div className="save-toast"><Check /> {savedMessage}</div>}

        {section === "products" && <>
          <div className="admin-stats"><article><small>Products</small><strong>{productRows.length}</strong><span><i /> {productRows.filter(item => item.stock === "In stock").length} in stock</span></article><article><small>New enquiries</small><strong>{leadState.filter(item => item.status === "New").length}</strong><span>Waiting for a reply</span></article><article><small>Featured items</small><strong>{productRows.filter(item => item.featured).length}</strong><span>Shown on the homepage</span></article></div>
          {showProductForm && editing && <form className="admin-editor" onSubmit={saveProduct}>
            <div className="admin-table-heading"><div><h2>{productRows.some(item => item.id === editing.id) ? "Edit product" : "Add a product"}</h2><p>Changes appear in the local catalogue immediately.</p></div><button type="button" className="icon-button" onClick={() => setShowProductForm(false)}><X /></button></div>
            <div className="admin-form-grid">
              <label>Product name<input required value={editing.name} onChange={event => setEditing({ ...editing, name: event.target.value })} /></label>
              <label>Price (₦)<input required min="1" type="number" value={editing.price || ""} onChange={event => setEditing({ ...editing, price: Number(event.target.value) })} /></label>
              <label>Category<select value={editing.categoryId} onChange={event => setEditing({ ...editing, categoryId: Number(event.target.value) })}>{categories.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
              <label>Stock<select value={editing.stock} onChange={event => setEditing({ ...editing, stock: event.target.value as Product["stock"] })}><option>In stock</option><option>Low stock</option><option>Made to order</option></select></label>
              <label className="admin-field-wide">Description<textarea rows={3} required value={editing.description} onChange={event => setEditing({ ...editing, description: event.target.value })} /></label>
              <label>Photo URL<input value={editing.image} onChange={event => setEditing({ ...editing, image: event.target.value })} /></label>
              <label className="admin-check"><input type="checkbox" checked={Boolean(editing.featured)} onChange={event => setEditing({ ...editing, featured: event.target.checked })} /> Feature on homepage</label>
            </div>
            <div className="admin-form-actions"><button type="button" className="button button-outline" onClick={() => setShowProductForm(false)}>Cancel</button><button className="button"><Check /> Save product</button></div>
          </form>}
          <div className="admin-table-card">
            <div className="admin-table-heading"><div><h2>Your products</h2><p>Keep prices and availability up to date.</p></div><button className="button" onClick={beginAdd}><Plus /> Add product</button></div>
            <div className="table-scroll"><table><thead>{table.getHeaderGroups().map(group => <tr key={group.id}>{group.headers.map(header => <th key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</th>)}</tr>)}</thead><tbody>{table.getRowModel().rows.map(row => <tr key={row.id}>{row.getVisibleCells().map(cell => <td key={cell.id} data-label={String(cell.column.columnDef.header ?? "")}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>)}</tr>)}</tbody></table></div>
          </div>
        </>}

        {section === "categories" && <div className="admin-panel">
          <div className="admin-table-heading"><div><h2>Shop categories</h2><p>Control which shelves customers can browse.</p></div><span className="panel-count"><Boxes /> {categoryState.filter(item => item.visible).length} visible</span></div>
          <div className="admin-list">{categoryState.map((category, i) => <div key={category.id}><span className={`icon-orbit ${category.tone}`}>{(() => { const Icon = categoryIcons[i]; return <Icon />; })()}</span><div><strong>{category.name}</strong><p>{category.description}</p></div><button className={`switch ${category.visible ? "on" : ""}`} onClick={() => setCategoryState(items => items.map(item => item.id === category.id ? { ...item, visible: !item.visible } : item))} aria-pressed={category.visible}><span /></button></div>)}</div>
        </div>}

        {section === "reviews" && <div className="admin-panel">
          <div className="admin-table-heading"><div><h2>Customer reviews</h2><p>Publish or hide testimonials across the storefront.</p></div><span className="panel-count"><HeartHandshake /> {reviewState.filter(item => item.published).length} published</span></div>
          <div className="admin-list reviews-admin">{reviewState.map(review => <div key={review.id}><span className="review-avatar">{review.name[0]}</span><div><strong>{review.name}</strong><p>“{review.quote}” · {review.source}</p></div><button className={`switch ${review.published ? "on" : ""}`} onClick={() => setReviewState(items => items.map(item => item.id === review.id ? { ...item, published: !item.published } : item))} aria-pressed={review.published}><span /></button></div>)}</div>
        </div>}

        {section === "enquiries" && <div className="admin-panel">
          <div className="admin-table-heading"><div><h2>Enquiry inbox</h2><p>Keep customer follow-ups organised.</p></div><button className="button button-outline" onClick={exportLeads}><Download /> Export CSV</button></div>
          <div className="lead-list">{leadState.map(lead => <article key={lead.id}><div className="lead-top"><span className={`status-pill ${lead.status.toLowerCase()}`}>{lead.status}</span><small>{lead.date}</small></div><h3>{lead.name} <a href={`tel:${lead.phone.replaceAll(" ", "")}`}>{lead.phone}</a></h3><p>{lead.message}</p><div><a className="text-link" href={whatsapp(`Hi ${lead.name.split(" ")[0]}, thanks for contacting Pumbaalicious about your enquiry.`)} target="_blank" rel="noreferrer"><MessageCircle /> Reply on WhatsApp</a>{lead.status === "New" && <button onClick={() => setLeadState(items => items.map(item => item.id === lead.id ? { ...item, status: "Replied" } : item))}><Check /> Mark replied</button>}</div></article>)}</div>
        </div>}

        {section === "settings" && <form className="admin-panel settings-panel" onSubmit={event => { event.preventDefault(); localStorage.setItem("pumba-admin-settings", JSON.stringify(settingsState)); setSavedMessage("Store settings saved."); window.setTimeout(() => setSavedMessage(""), 2400); }}>
          <div className="admin-table-heading"><div><h2>Store settings</h2><p>Update customer-facing contact and service details.</p></div><ThemeToggle /></div>
          <div className="admin-form-grid">{[
            ["phone", "Display phone"],
            ["whatsapp", "WhatsApp number"],
            ["hours", "Opening hours"],
            ["address", "Store address"],
            ["delivery", "Delivery options"],
            ["payment", "Payment options"]
          ].map(([key, label]) => <label key={key}>{label}<input value={settingsState[key]} onChange={event => setSettingsState({ ...settingsState, [key]: event.target.value })} /></label>)}</div>
          <div className="admin-form-actions"><button className="button"><Check /> Save settings</button></div>
        </form>}

        <p className="admin-note"><ShieldCheck /> Dashboard changes are stored in this browser. Connect staff authentication before sharing admin access.</p>
      </div>
    </section>
  );
}

function NotFound() {
  usePageTitle("Page not found");
  return <section className="not-found section-pad"><span>404</span><h1>That shelf is empty.</h1><p>The page you’re looking for may have moved.</p><Link className="button" to="/">Back to the shop</Link></section>;
}

function StructuredData() {
  const data = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "Store"],
    name: "Pumbaalicious Store",
    url: "https://pumbaalicious.pages.dev",
    telephone: "+2347062025462",
    foundingDate: "2015",
    currenciesAccepted: "NGN",
    address: { "@type": "PostalAddress", streetAddress: "House 5, Gowon Estate", addressLocality: "Egbeda", addressRegion: "Lagos", addressCountry: "NG" },
    areaServed: ["Egbeda", "Alimosho", "Lagos"],
    openingHoursSpecification: [
      { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "10:00", closes: "17:00" },
      { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "11:00", closes: "16:00" }
    ],
    aggregateRating: { "@type": "AggregateRating", ratingValue: "5", reviewCount: "2" }
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />;
}

export default function App() {
  return (
    <>
      <StructuredData />
      <Routes>
        <Route path="/admin" element={<AdminPage />} />
        <Route path="*" element={<Layout><Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/shop/:slug" element={<ShopPage />} />
          <Route path="/product/:slug" element={<ProductPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/delivery" element={<DeliveryPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes></Layout>} />
      </Routes>
    </>
  );
}
